import { useState } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as Popover from "@radix-ui/react-popover";
import { Info, ChevronDown } from "lucide-react";

// Contextual help. Short `text` shows on hover (tooltip). When `details` is provided,
// the icon opens a click popover whose body has an expand/collapse "Show more" section
// (so long explanations stay tidy and work on touch devices).
export function InfoTip({ text, details, title, testid, side = "top", iconSize = 14, className = "" }) {
  const [more, setMore] = useState(false);
  const trigger = (
    <button
      type="button"
      data-testid={testid}
      aria-label={title ? `Help: ${title}` : "More information"}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center justify-center text-gray-300 hover:text-indigo-500 focus:text-indigo-500 focus:outline-none transition-colors align-middle ${className}`}
    >
      <Info size={iconSize} />
    </button>
  );

  if (!details) {
    return (
      <Tooltip.Provider delayDuration={120}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>{trigger}</Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side={side}
              sideOffset={6}
              collisionPadding={12}
              className="z-[70] max-w-[18rem] rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-lg animate-in fade-in-0 zoom-in-95"
            >
              {title && <div className="font-semibold mb-0.5">{title}</div>}
              <span className="text-gray-100">{text}</span>
              <Tooltip.Arrow className="fill-gray-900" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  }

  return (
    <Popover.Root onOpenChange={(o) => !o && setMore(false)}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-[70] w-80 max-w-[calc(100vw-1.5rem)] rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700 shadow-xl animate-in fade-in-0 zoom-in-95"
        >
          {title && (
            <div className="font-semibold text-gray-900 mb-1 flex items-center gap-1.5">
              <Info size={14} className="text-indigo-500 shrink-0" /> {title}
            </div>
          )}
          <p className="text-[13px] leading-relaxed text-gray-600">{text}</p>
          <button
            type="button"
            data-testid={testid ? `${testid}-toggle` : undefined}
            onClick={() => setMore((m) => !m)}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <ChevronDown size={13} className={`transition-transform ${more ? "rotate-180" : ""}`} />
            {more ? "Show less" : "Show more"}
          </button>
          {more && (
            <div className="mt-2 pt-2 border-t border-gray-100 text-[13px] leading-relaxed text-gray-600 space-y-1.5">
              {details}
            </div>
          )}
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
